import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { StudentObservation } from '../../lib/liveLessonTypes';
import { Button, Chip, Modal } from '../ui';

type LiveSessionReviewProps = {
  isOpen: boolean;
  observations: StudentObservation[];
  onClose: () => void;
  onCommit: (ids: string[]) => Promise<void>;
  onDiscard: (ids: string[]) => Promise<void>;
};

export default function LiveSessionReview({
  isOpen,
  observations,
  onClose,
  onCommit,
  onDiscard
}: LiveSessionReviewProps) {
  const draftIds = useMemo(
    () => observations.filter((entry) => entry.status === 'draft').map((entry) => entry.id),
    [observations]
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [busyAction, setBusyAction] = useState<'commit' | 'discard' | null>(null);
  const effectiveSelectedIds = selectedIds.length > 0 ? selectedIds : draftIds;

  useEffect(() => {
    if (!isOpen) {
      setSelectedIds([]);
      setBusyAction(null);
    }
  }, [isOpen]);

  const runAction = async (action: 'commit' | 'discard') => {
    if (busyAction) return;
    setBusyAction(action);
    try {
      if (action === 'commit') {
        await onCommit(effectiveSelectedIds);
      } else {
        await onDiscard(effectiveSelectedIds);
      }
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Stunden-Review" size="lg">
      <div className="space-y-3">
        <div className="text-sm text-gray-600">
          Temporäre Beobachtungen können jetzt dauerhaft übernommen oder verworfen werden.
        </div>
        <div className="max-h-72 space-y-2 overflow-auto rounded-lg border border-gray-200 p-2">
          {observations.map((entry) => (
            <button
              type="button"
              key={entry.id}
              onClick={() =>
                setSelectedIds((prev) =>
                  prev.includes(entry.id) ? prev.filter((id) => id !== entry.id) : [...prev, entry.id]
                )
              }
              className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left ${
                selectedIds.includes(entry.id) ? 'border-primary-500 bg-primary-50' : 'border-gray-200'
              }`}
            >
              <span className="text-sm font-medium text-gray-900">
                {entry.studentId} · {entry.preset}
              </span>
              <Chip size="sm" variant="outline">
                {entry.status}
              </Chip>
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="primary"
            disabled={Boolean(busyAction)}
            onClick={() => void runAction('commit')}
          >
            {busyAction === 'commit' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Übernehmen
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={Boolean(busyAction)}
            onClick={() => void runAction('discard')}
          >
            {busyAction === 'discard' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Verwerfen
          </Button>
        </div>
      </div>
    </Modal>
  );
}
